import {
  findSeasonOverlaps, isoToMonthDay, nextOccurrenceIso, parseMonthDay, todayIso,
} from '../utils/pricingSeasons';

/**
 * SAM-589 — seasonal pricing: pure form <-> payload logic (no React, no baseui).
 *
 * Maps to `service.pricing.seasons`, the shape `functions/booking/pricing.py`
 * (`apply_season` / `validate_seasons`) reads:
 *
 *   { "id": "high-2026", "label": {"en": "High season", "es": "Temporada alta"},
 *     "start_date": "2026-12-15", "end_date": "2027-04-15",           // inclusive
 *     "prices": { "<option_id>": { "price": 120,
 *                                  "prices_by_group": {"intl": 150},
 *                                  "prices_by_role": {"3": 90} } } }
 *
 * Integration (SAM-591 / SAM-592): a season can also replace `extra_guest_price`
 * (unit-priced services) and merge `prices_by_event_size` per band (event-priced
 * services) — the backend's `apply_season` treats them exactly like `price` and
 * `prices_by_role`. Both are optional in the form so older rows stay valid.
 *
 * SAM-626 — a season can recur: `every_year: true` with `start_date`/`end_date` as `MM-DD`
 * (start after end = crosses the year end; `02-29` clamps to `02-28` in non-leap years). The key
 * is absent on fixed seasons, never `false`. Overlap is checked with utils/pricingSeasons.ts,
 * a port of the backend's `pricing_seasons.py`.
 *
 * Numbers live as strings in the form so a blank cell ("use the base price")
 * stays distinguishable from 0; `serializeSeasons` turns them back and omits
 * blanks. Overlap validation mirrors the backend rule (bounds inclusive: a
 * season ending the day another starts overlaps).
 */

export interface SeasonPriceForm {
  price: string;
  prices_by_group: Record<string, string>;
  prices_by_role: Record<string, string>;
  /** SAM-591 — per-extra-guest charge override (unit-priced services). */
  extra_guest_price?: string;
  /** SAM-592 — per-band override (event-priced services), merged per key. */
  prices_by_event_size?: Record<string, string>;
}

export interface SeasonForm {
  id: string;
  label_en: string;
  label_es: string;
  /** SAM-626 — recurring season: start/end hold `MM-DD`. Absent (never false) on fixed seasons. */
  every_year?: boolean;
  start_date: string;
  end_date: string;
  /** Keyed by booking option id. A missing entry means "base price". */
  prices: Record<string, SeasonPriceForm>;
}

export interface ServiceSeasonPayload {
  id: string;
  label?: { en?: string; es?: string };
  /** SAM-626 — exactly `true` or absent. */
  every_year?: true;
  start_date: string;
  end_date: string;
  prices: Record<string, {
    price?: number;
    prices_by_group?: Record<string, number>;
    prices_by_role?: Record<string, number>;
    extra_guest_price?: number;
    prices_by_event_size?: Record<string, number>;
  }>;
}

export type SeasonPriceField =
  | 'price' | 'extra_guest_price'
  | 'prices_by_group' | 'prices_by_role' | 'prices_by_event_size';

/** Fields a season REPLACES (the rest are maps merged per key). */
export const SEASON_SCALAR_FIELDS: readonly SeasonPriceField[] = ['price', 'extra_guest_price'];

export const emptySeasonPrice = (): SeasonPriceForm => ({ price: '', prices_by_group: {}, prices_by_role: {} });

const stringify = (value: unknown): string => (value == null ? '' : String(value));

const stringifyMap = (map: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (map && typeof map === 'object') {
    Object.entries(map as Record<string, unknown>).forEach(([k, v]) => { out[k] = stringify(v); });
  }
  return out;
};

export function parseSeasons(seasons: ServiceSeasonPayload[] | undefined | null): SeasonForm[] {
  if (!Array.isArray(seasons)) return [];
  return seasons.map((season, i) => {
    const prices: Record<string, SeasonPriceForm> = {};
    Object.entries(season.prices || {}).forEach(([optionId, entry]) => {
      prices[optionId] = {
        price: stringify(entry?.price),
        prices_by_group: stringifyMap(entry?.prices_by_group),
        prices_by_role: stringifyMap(entry?.prices_by_role),
        ...(entry?.extra_guest_price != null ? { extra_guest_price: stringify(entry.extra_guest_price) } : {}),
        ...(entry?.prices_by_event_size ? { prices_by_event_size: stringifyMap(entry.prices_by_event_size) } : {}),
      };
    });
    return {
      id: season.id || `season-${i + 1}`,
      label_en: season.label?.en || '',
      label_es: season.label?.es || '',
      ...(season.every_year === true ? { every_year: true } : {}),
      start_date: season.start_date || '',
      end_date: season.end_date || '',
      prices,
    };
  });
}

const numericMap = (map: Record<string, string>): Record<string, number> => {
  const out: Record<string, number> = {};
  Object.entries(map).forEach(([k, v]) => {
    if (v.trim() !== '' && !Number.isNaN(Number(v))) out[k] = Number(v);
  });
  return out;
};

/** Next unused `season-N` id for a new row. */
export function nextSeasonId(existing: SeasonForm[]): string {
  const used = new Set(existing.map((s) => s.id));
  let n = existing.length + 1;
  while (used.has(`season-${n}`)) n += 1;
  return `season-${n}`;
}

/**
 * Save payload. Rows without both dates are dropped (a half-filled row is not
 * a rule); price entries for options not in `optionIds` are dropped so a
 * removed booking option never leaves an orphan override; blank cells are
 * omitted (= base price).
 */
export function serializeSeasons(seasons: SeasonForm[], optionIds?: string[]): ServiceSeasonPayload[] {
  return seasons
    .filter((s) => s.start_date && s.end_date)
    .map((s) => {
      const label: { en?: string; es?: string } = {};
      if (s.label_en.trim()) label.en = s.label_en.trim();
      if (s.label_es.trim()) label.es = s.label_es.trim();
      const prices: ServiceSeasonPayload['prices'] = {};
      Object.entries(s.prices).forEach(([optionId, entry]) => {
        if (optionIds && !optionIds.includes(optionId)) return;
        const out: ServiceSeasonPayload['prices'][string] = {};
        if (entry.price.trim() !== '' && !Number.isNaN(Number(entry.price))) out.price = Number(entry.price);
        const groups = numericMap(entry.prices_by_group);
        if (Object.keys(groups).length) out.prices_by_group = groups;
        const roles = numericMap(entry.prices_by_role);
        if (Object.keys(roles).length) out.prices_by_role = roles;
        const extra = entry.extra_guest_price ?? '';
        if (extra.trim() !== '' && !Number.isNaN(Number(extra))) out.extra_guest_price = Number(extra);
        const bands = numericMap(entry.prices_by_event_size || {});
        if (Object.keys(bands).length) out.prices_by_event_size = bands;
        if (Object.keys(out).length) prices[optionId] = out;
      });
      return {
        id: s.id,
        ...(Object.keys(label).length ? { label } : {}),
        ...(s.every_year === true ? { every_year: true as const } : {}),
        start_date: s.start_date,
        end_date: s.end_date,
        prices,
      };
    });
}

/**
 * SAM-624 — drop a rate group's price cells from every season. Removing a group from the service
 * would otherwise leave `prices_by_group[<id>]` behind in each season override: `serializeSeasons`
 * does not prune group ids, and the cell would resurrect (with a stale price) if a new group
 * re-used the id. A season entry left with nothing in it stays as an empty override (= base
 * prices), which serializes away. Pure: returns new objects only where something changed.
 */
export function removeGroupFromSeasons(seasons: SeasonForm[], groupId: string): SeasonForm[] {
  const id = groupId.trim();
  if (!id) return seasons;
  return seasons.map((season) => {
    let touched = false;
    const prices: Record<string, SeasonPriceForm> = {};
    Object.entries(season.prices).forEach(([optionId, entry]) => {
      if (Object.prototype.hasOwnProperty.call(entry.prices_by_group, id)) {
        touched = true;
        const { [id]: _removed, ...rest } = entry.prices_by_group;
        prices[optionId] = { ...entry, prices_by_group: rest };
      } else {
        prices[optionId] = entry;
      }
    });
    return touched ? { ...season, prices } : season;
  });
}

/** A rate group's id changed: move its season override cells to the new id (empty id drops them). */
export function renameGroupInSeasons(seasons: SeasonForm[], oldId: string, newId: string): SeasonForm[] {
  const from = oldId.trim();
  const to = newId.trim();
  if (!from || from === to) return seasons;
  return seasons.map((season) => {
    let touched = false;
    const prices: Record<string, SeasonPriceForm> = {};
    Object.entries(season.prices).forEach(([optionId, entry]) => {
      if (Object.prototype.hasOwnProperty.call(entry.prices_by_group, from)) {
        touched = true;
        const { [from]: value, ...rest } = entry.prices_by_group;
        prices[optionId] = { ...entry, prices_by_group: to ? { ...rest, [to]: value } : rest };
      } else {
        prices[optionId] = entry;
      }
    });
    return touched ? { ...season, prices } : season;
  });
}

export interface SeasonErrors {
  /** Per-season-index messages. */
  bySeason: Record<number, string[]>;
  hasErrors: boolean;
}


/**
 * SAM-626 — flip a season between fixed dates and "every year", converting the bounds instead of
 * blanking them: ISO -> its MM-DD; MM-DD -> the next occurrence as ISO (the end is the first
 * occurrence on/after that start, so a year-crossing range keeps its length). Turning OFF
 * removes the `every_year` key. `today` is injectable for tests.
 */
export function setSeasonEveryYear(season: SeasonForm, on: boolean, today: string = todayIso()): SeasonForm {
  const { every_year: _drop, ...rest } = season;
  if (on) {
    return {
      ...rest,
      every_year: true,
      start_date: isoToMonthDay(season.start_date),
      end_date: isoToMonthDay(season.end_date),
    };
  }
  const start = nextOccurrenceIso(season.start_date, today);
  const end = start ? nextOccurrenceIso(season.end_date, start) : '';
  return { ...rest, start_date: start, end_date: end };
}

const seasonName = (s: SeasonForm): string => s.label_en || s.label_es || s.id;

/**
 * Editor-side validation: both dates present, end >= start (fixed seasons only: a recurring
 * range may cross the year end), no negative or non-numeric price, and no two seasons sharing a
 * day in any year (inclusive bounds; fixed x fixed, recurring x recurring and fixed x recurring,
 * exactly as the CRUD save checks it via utils/pricingSeasons.ts). Every overlapping pair is
 * flagged on both rows. ISO `YYYY-MM-DD` strings compare correctly as plain strings.
 */
export function validateSeasonForms(seasons: SeasonForm[]): SeasonErrors {
  const bySeason: Record<number, string[]> = {};
  const add = (i: number, msg: string) => {
    const list = (bySeason[i] ||= []);
    if (!list.includes(msg)) list.push(msg);
  };

  seasons.forEach((s, i) => {
    if (!s.start_date || !s.end_date) {
      add(i, s.every_year ? 'Set a start and an end day' : 'Set a start and an end date');
    } else if (s.every_year) {
      if (!parseMonthDay(s.start_date) || !parseMonthDay(s.end_date)) add(i, 'Pick a valid start and end day');
    } else if (s.end_date < s.start_date) {
      add(i, 'End date is before the start date');
    }
    const cells = Object.values(s.prices).flatMap((e) => [
      e.price, ...Object.values(e.prices_by_group), ...Object.values(e.prices_by_role),
      e.extra_guest_price ?? '', ...Object.values(e.prices_by_event_size || {}),
    ]);
    if (cells.some((v) => v.trim() !== '' && (Number.isNaN(Number(v)) || Number(v) < 0))) {
      add(i, 'Prices must be 0 or more');
    }
  });

  // Rows the per-row checks accept; the helper skips anything malformed anyway.
  const candidates = seasons
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.start_date && s.end_date && (s.every_year || s.end_date >= s.start_date));
  findSeasonOverlaps(candidates.map(({ s }) => ({
    id: s.id,
    every_year: s.every_year === true ? true : undefined,
    start_date: s.start_date,
    end_date: s.end_date,
  }))).forEach(([a, b]) => {
    const ia = candidates[a].i;
    const ib = candidates[b].i;
    add(ia, `Overlaps another season (${seasonName(seasons[ib])})`);
    add(ib, `Overlaps another season (${seasonName(seasons[ia])})`);
  });

  return { bySeason, hasErrors: Object.keys(bySeason).length > 0 };
}
